import { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Tabs, Tab, ProgressBar } from "react-bootstrap";
import toast from "react-hot-toast";
import Sidebar from "../../layout/Sidebar/SideBar";
import Navbar from "../../layout/Navbar/Navbar";
import blogFetch from "../../axios/config";
import { validarCPF, validarCNPJ, maskCPF, maskCNPJ } from "../../services/valid";

const EMPRESA = {
  razaoSocial: "", nomeFantasia: "", cnpj: "", responsavel: "", email: "", telefone: "",
  tipoServico: "", gestorResponsavel: "", nivelRisco: "", status: "Ativa", observacoes: "",
};
const COLABORADOR = {
  nome: "", cpf: "", matricula: "", empresaId: "", funcao: "",
  dataAdmissao: "", validadeVinculo: "", telefone: "", email: "",
};
const DOCUMENTO = { colaboradorId: "", tipo: "", numero: "", dataEmissao: "", dataValidade: "" };

const NIVEIS_RISCO = ["Baixo", "Médio", "Alto"];
const STATUS_EMPRESA = ["Ativa", "Em análise", "Inativa", "Bloqueada"];

// input type="date" (YYYY-MM-DD) -> int YYYYMMDD, padrão de datas do projeto
const dataInt = (valor) => Number(valor.replaceAll("-", ""));

// Liga um input ao campo `nome` do objeto de estado.
const alterar = (setObj, nome, valor) => setObj((atual) => ({ ...atual, [nome]: valor }));

const campo = (obj, setObj, nome, props = {}) => (
  <Form.Control value={obj[nome]} onChange={(e) => alterar(setObj, nome, e.target.value)} {...props} />
);

// opcoes: lista de textos ou de { valor, texto }
const selecao = (obj, setObj, nome, placeholder, opcoes) => (
  <Form.Select value={obj[nome]} onChange={(e) => alterar(setObj, nome, e.target.value)}>
    {placeholder && <option value="">{placeholder}</option>}
    {opcoes.map((o) => {
      const { valor, texto } = typeof o === "string" ? { valor: o, texto: o } : o;
      return <option key={valor} value={valor}>{texto}</option>;
    })}
  </Form.Select>
);

export const CadastroHse = () => {
  const [empresas, setEmpresas] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);

  const [empresa, setEmpresa] = useState(EMPRESA);
  const [colaborador, setColaborador] = useState(COLABORADOR);
  const [documento, setDocumento] = useState(DOCUMENTO);
  const [arquivo, setArquivo] = useState(null);
  const [progresso, setProgresso] = useState(0);

  const getDados = async () => {
    try {
      const [emp, col, reg, tip] = await Promise.all([
        blogFetch.get("/sga/company"),
        blogFetch.get("/sga/collaborator"),
        blogFetch.get("/sga/rules"),
        blogFetch.get("/sga/document-type"),
      ]);
      setEmpresas(emp.data);
      setColaboradores(col.data);
      setTiposServico(reg.data.map((r) => r.tipoServico));
      setTiposDocumento(tip.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getDados();
  }, []);

  const salvarEmpresa = async () => {
    const e = empresa;
    if (!e.razaoSocial || !e.responsavel || !e.tipoServico || !e.gestorResponsavel || !e.nivelRisco) {
      toast.error("Preencha todos os campos obrigatórios!");
      return;
    }
    if (!validarCNPJ(e.cnpj)) {
      toast.error("CNPJ inválido.");
      return;
    }
    try {
      await blogFetch.post("/sga/company", e);
      await getDados();
      toast.success("Empresa cadastrada com sucesso!");
      setEmpresa(EMPRESA);
    } catch (err) {
      toast.error("Erro ao cadastrar a empresa.");
    }
  };

  const salvarColaborador = async () => {
    const c = colaborador;
    if (!c.nome || !c.empresaId || !c.funcao || !c.dataAdmissao || !c.validadeVinculo) {
      toast.error("Preencha todos os campos obrigatórios!");
      return;
    }
    if (!validarCPF(c.cpf)) {
      toast.error("CPF inválido.");
      return;
    }
    if (c.validadeVinculo < c.dataAdmissao) {
      toast.error("A validade do vínculo deve ser igual ou posterior à admissão.");
      return;
    }
    const payload = {
      ...c,
      empresaId: Number(c.empresaId),
      dataAdmissao: dataInt(c.dataAdmissao),
      validadeVinculo: dataInt(c.validadeVinculo),
    };
    try {
      await blogFetch.post("/sga/collaborator", payload);
      await getDados();
      toast.success("Colaborador cadastrado com sucesso!");
      setColaborador(COLABORADOR);
    } catch (err) {
      toast.error("Erro ao cadastrar o colaborador.");
    }
  };

  const salvarDocumento = async () => {
    const d = documento;
    if (!d.colaboradorId || !d.tipo || !d.dataEmissao || !d.dataValidade) {
      toast.error("Preencha todos os campos obrigatórios!");
      return;
    }
    if (d.dataValidade < d.dataEmissao) {
      toast.error("A validade deve ser igual ou posterior à emissão.");
      return;
    }
    if (!arquivo) {
      toast.error("Anexe o arquivo do documento.");
      return;
    }

    const formData = new FormData();
    formData.append("colaboradorId", d.colaboradorId);
    formData.append("tipo", d.tipo);
    formData.append("numero", d.numero);
    formData.append("dataEmissao", dataInt(d.dataEmissao));
    formData.append("dataValidade", dataInt(d.dataValidade));
    formData.append("arquivo", arquivo);

    try {
      await blogFetch.post("/sga/document", formData, {
        onUploadProgress: (e) => setProgresso(Math.min(99, Math.round((e.loaded * 100) / e.total))),
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento enviado para análise do HSE!");
      setDocumento(DOCUMENTO);
      setArquivo(null);
    } catch (err) {
      toast.error("Erro ao enviar o documento.");
    } finally {
      setProgresso(0);
    }
  };

  const empresasAtivas = empresas
    .filter((e) => e.status === "Ativa")
    .map((e) => ({ valor: e.id, texto: e.nomeFantasia }));
  const opcoesColaboradores = colaboradores.map((c) => ({ valor: c.id, texto: `${c.nome} - ${c.empresa}` }));

  return (
    <>
      <Sidebar />
      <Navbar />
      <div className="app_body">
        <Container className="py-4">
          <div className="p-4 shadow-sm">
            <h3 className="fw-bold text-center mb-4">Cadastro de Terceiros</h3>

            <Tabs defaultActiveKey="empresa" className="mb-4" fill>
              <Tab eventKey="empresa" title="Empresa">
                <Row className="g-3">
                  <Col md={6}>{campo(empresa, setEmpresa, "razaoSocial", { placeholder: "Razão social *" })}</Col>
                  <Col md={6}>{campo(empresa, setEmpresa, "nomeFantasia", { placeholder: "Nome fantasia" })}</Col>
                  <Col md={4}>
                    <Form.Control
                      placeholder="CNPJ *"
                      value={empresa.cnpj}
                      isInvalid={empresa.cnpj.length === 18 && !validarCNPJ(empresa.cnpj)}
                      isValid={validarCNPJ(empresa.cnpj)}
                      onChange={(e) => alterar(setEmpresa, "cnpj", maskCNPJ(e.target.value))}
                    />
                  </Col>
                  <Col md={4}>{campo(empresa, setEmpresa, "responsavel", { placeholder: "Responsável *" })}</Col>
                  <Col md={4}>{campo(empresa, setEmpresa, "gestorResponsavel", { placeholder: "Gestor responsável (AGP) *" })}</Col>
                  <Col md={4}>{campo(empresa, setEmpresa, "email", { placeholder: "E-mail", type: "email" })}</Col>
                  <Col md={4}>{campo(empresa, setEmpresa, "telefone", { placeholder: "Telefone" })}</Col>
                  <Col md={4}>{selecao(empresa, setEmpresa, "tipoServico", "Tipo de serviço *", tiposServico)}</Col>
                  <Col md={4}>{selecao(empresa, setEmpresa, "nivelRisco", "Nível de risco *", NIVEIS_RISCO)}</Col>
                  <Col md={4}>{selecao(empresa, setEmpresa, "status", null, STATUS_EMPRESA)}</Col>
                  <Col md={12}>{campo(empresa, setEmpresa, "observacoes", { placeholder: "Observações", as: "textarea", rows: 2 })}</Col>
                  <Col xs={12} className="text-end">
                    <Button className="px-5" onClick={salvarEmpresa}>Salvar</Button>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="colaborador" title="Colaborador">
                <Row className="g-3">
                  <Col md={6}>{campo(colaborador, setColaborador, "nome", { placeholder: "Nome completo *" })}</Col>
                  <Col md={3}>
                    <Form.Control
                      placeholder="CPF *"
                      value={colaborador.cpf}
                      isInvalid={colaborador.cpf.length === 14 && !validarCPF(colaborador.cpf)}
                      isValid={validarCPF(colaborador.cpf)}
                      onChange={(e) => alterar(setColaborador, "cpf", maskCPF(e.target.value))}
                    />
                  </Col>
                  <Col md={3}>{campo(colaborador, setColaborador, "matricula", { placeholder: "Matrícula / crachá" })}</Col>
                  <Col md={6}>{selecao(colaborador, setColaborador, "empresaId", "Empresa (somente ativas) *", empresasAtivas)}</Col>
                  <Col md={6}>{campo(colaborador, setColaborador, "funcao", { placeholder: "Função *" })}</Col>
                  <Col md={3}>
                    <Form.Label className="small mb-0">Admissão *</Form.Label>
                    {campo(colaborador, setColaborador, "dataAdmissao", { type: "date" })}
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small mb-0">Validade do vínculo *</Form.Label>
                    {campo(colaborador, setColaborador, "validadeVinculo", { type: "date", min: colaborador.dataAdmissao })}
                  </Col>
                  <Col md={3} className="align-self-end">{campo(colaborador, setColaborador, "telefone", { placeholder: "Telefone" })}</Col>
                  <Col md={3} className="align-self-end">{campo(colaborador, setColaborador, "email", { placeholder: "E-mail", type: "email" })}</Col>
                  <Col xs={12} className="text-end">
                    <Button className="px-5" onClick={salvarColaborador}>Salvar</Button>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="documento" title="Documento">
                <Row className="g-3">
                  <Col md={6}>{selecao(documento, setDocumento, "colaboradorId", "Colaborador *", opcoesColaboradores)}</Col>
                  <Col md={3}>{selecao(documento, setDocumento, "tipo", "Tipo *", tiposDocumento)}</Col>
                  <Col md={3}>{campo(documento, setDocumento, "numero", { placeholder: "Número" })}</Col>
                  <Col md={3}>
                    <Form.Label className="small mb-0">Emissão *</Form.Label>
                    {campo(documento, setDocumento, "dataEmissao", { type: "date" })}
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small mb-0">Validade *</Form.Label>
                    {campo(documento, setDocumento, "dataValidade", { type: "date", min: documento.dataEmissao })}
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small mb-0">Arquivo (PDF ou imagem) *</Form.Label>
                    <Form.Control
                      key={arquivo ? "com-arquivo" : "sem-arquivo"}
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => setArquivo(e.target.files[0] || null)}
                    />
                  </Col>
                  {progresso > 0 && (
                    <Col xs={12}>
                      <ProgressBar now={progresso} label={`${progresso}%`} />
                    </Col>
                  )}
                  <Col xs={12} className="text-end">
                    <Button className="px-5" onClick={salvarDocumento}>Enviar para análise</Button>
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </div>
        </Container>
      </div>
    </>
  );
};
